import { TrophyIcon } from "@/components/Icons/trophy-icon";
import { Skeleton } from "@/components/ui/skeleton";
import type { LeaderboardCycle } from "@/hooks/useGetActiveLeaderboardCycle";
import { useAuthStore } from "@/store/auth.store";
import { pointsToYen } from "@/utils";
import { ChevronRightLeaderBoard, ThumbaTrophyIcon, TooltipArrow } from "@roman/shared/components/Icons";
import { cn } from "@roman/shared/utils";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RulesModel } from "./RulesModal";

const rewardTextStyle = {
  background: "linear-gradient(180deg, rgb(243, 182, 0) 50%, rgb(202, 138, 0) 50%)",
  backgroundClip: "text",
  color: "transparent",
};

const RewardAmount = ({ amount }: { amount: number }) => (
  <span
    className="text-right font-oswald text-[40px] font-bold leading-[1] bg-gradient-to-b from-[#F3B600] via-[#F3B600] to-[#CA8A00] bg-clip-text text-transparent"
    style={rewardTextStyle}
  >
    {pointsToYen(amount)}
  </span>
);

const CurrentRankInfo = ({
  freePointsToNextRank,
  currentRank,
  setIsShowRule,
  isShowPointGap = false,
  isShowRules = true,
  potentialRedeemablePoints,
  leaderboardCycle,
}: {
  freePointsToNextRank?: string;
  currentRank?: number;
  potentialRedeemablePoints?: string;
  setIsShowRule: (v: boolean) => void;
  isShowPointGap?: boolean;
  isShowRules?: boolean;
  leaderboardCycle: LeaderboardCycle;
}) => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.authState === "authenticated");
  const isLongBubleText = freePointsToNextRank && freePointsToNextRank.length > 6;
  const points = Number(freePointsToNextRank ?? 0);

  return (
    <div className="relative">
      <div className="text-dark-gray font-noto-sans-jp text-[10px] font-medium leading-[1.4]">
        {leaderboardCycle.concludedAt ? "ランキング結果" : "現在ランキング"}
      </div>
      <div className="text-right font-roboto text-[18px] font-bold leading-[1]">
        <span>{currentRank ?? "- "}</span>
        <span className="text-right text-dark-gray font-noto-sans-jp text-[12px] font-medium leading-[1]">位</span>
      </div>

      <div className="text-dark-gray font-noto-sans-jp text-[10px] font-medium leading-[1.4]">
        {leaderboardCycle.concludedAt ? "確定報酬" : "予定報酬"}
      </div>
      <div className="text-right font-roboto text-[26px] font-extrabold leading-[1] text-orange flex justify-end items-center gap-1">
        <ThumbaTrophyIcon className="w-5 h-5" />
        <div>
          <span
            style={{
              animation: "blink 1.5s cubic-bezier(0, 0, 0.2, 1) infinite",
            }}
          >
            {pointsToYen(potentialRedeemablePoints ?? 0)}
          </span>
          <span className="font-roboto text-[12px] font-medium leading-[1]">円</span>
        </div>
      </div>

      {isShowRules && (
        <button type="button" onClick={() => setIsShowRule(true)} className="mt-1 w-full">
          <div className="flex items-center justify-end gap-[2px]">
            <div className="w-[15px] h-[15px] rounded-full border-[0.9px] border-primary flex items-center justify-center">
              <span className="text-center font-noto-sans-jp text-[8px] font-bold text-primary">i</span>
            </div>
            <div className="text-right font-noto-sans-jp text-[10px] font-medium text-primary">ルールを見る</div>
          </div>
        </button>
      )}
      {isShowPointGap && points > 0 && (
        <div className="absolute top-0 w-full" onClick={() => navigate("/leaderboard")}>
          <div
            className={cn(
              "animate-bubbleUpDown flex justify-between items-center text-white border-0 z-2 py-[3px] px-2 relative bg-transparent shadow-none top-[-30px] left-[-45px] h-[35px]",
              isLongBubleText && "left-[-60px]",
            )}
          >
            <div
              className="relative w-auto bg-orange px-[6px] rounded-sm h-[26px] flex items-center justify-center min-h-[35px]"
              style={{ boxShadow: "2px 2px 2px 0px rgba(88, 40, 0, 0.14)" }}
            >
              <div className="flex items-center justify-center">
                <div className="flex items-center space-x-1 leading-[14px] justify-center">
                  <span className="font-bold text-[12px] text-white whitespace-nowrap">
                    次の順位まであと
                    <span className="font-extrabold">{isAuthenticated ? freePointsToNextRank : "- "}</span>
                    pt
                  </span>
                </div>
                <ChevronRightLeaderBoard className="ml-1" />
              </div>
              <div className={cn("absolute -bottom-2 text-orange w-3 h-3 left-4", isLongBubleText && "left-7")}>
                <TooltipArrow />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const LeaderboardInfoCard = ({
  leaderboardCycle,
  currentRank,
  potentialRedeemablePoints,
  isShowPointGap = false,
  isShowRulesButton = true,
  freePointsToNextRank,
}: {
  leaderboardCycle: LeaderboardCycle;
  currentRank?: number;
  potentialRedeemablePoints?: string;
  freePointsToNextRank?: string;
  isShowPointGap?: boolean;
  isShowRulesButton?: boolean;
}) => {
  const [isShowRules, setIsShowRule] = useState(false);
  return (
    <>
      <div className="divide-x-[0.5px] divide-dashed divide-orange py-[6px] px-[13.08px] border-b bg-orange/10 flex justify-between">
        <div className="w-[240px]">
          <div className="flex gap-1">
            <div className="py-1">
              <TrophyIcon className="w-5 h-5" color="#CA8A02" />
            </div>
            <div>
              <span className="text-black font-noto-sans-jp text-[14px] font-medium">ランキング報酬</span>
              <div className="font-oswald">
                <RewardAmount amount={leaderboardCycle.totalAmount} />
                <span className="text-gray-700 text-lg font-medium ml-1">円</span>
              </div>
            </div>
          </div>
          <p className="text-black text-[10px] mt-1 font-semibold">
            シーズン終了時の順位で、
            <span className="text-orange font-bold">報酬</span> が付与されます
          </p>
        </div>
        <div className="pl-[17.73px] md:pl-3 lg:pl-7 pt-2 w-40">
          <CurrentRankInfo
            currentRank={currentRank}
            leaderboardCycle={leaderboardCycle}
            potentialRedeemablePoints={potentialRedeemablePoints}
            setIsShowRule={setIsShowRule}
            isShowPointGap={isShowPointGap}
            isShowRules={isShowRulesButton}
            freePointsToNextRank={freePointsToNextRank}
          />
        </div>
      </div>
      <RulesModel isShowRules={isShowRules} setIsShowRules={setIsShowRule} />
    </>
  );
};

export const LeaderboardInfoCardLoadingSkeleton = () => {
  return (
    <div className="h-[100px] w-full flex py-[6px] px-[13px] divide-x-[0.5px] divide-dashed divide-orange border-b bg-orange/10 justify-between">
      <div className="w-2/3">
        <Skeleton className="h-4 w-32 mb-2" />
        <div className="px-5">
          <Skeleton className="h-8 w-48 mb-2" />
        </div>

        <Skeleton className="h-4 w-2/3 mb-2" />
      </div>
      <div className="flex-1 pl-3">
        <Skeleton className="h-4 w-full mb-0.5" />
        <div className="flex justify-end w-full">
          <Skeleton className="h-4 w-5" />
        </div>
        <Skeleton className="h-4 w-20 mb-1" />
        <div className="flex justify-end">
          <div className="w-1/2">
            <Skeleton className="h-4 w-full md:w-24 mb-1" />
            <Skeleton className="h-4 w-full md:w-24 mb-1" />
          </div>
        </div>
      </div>
    </div>
  );
};
